package com.schoolconnect.app.feature.schools.details

import com.schoolconnect.app.datasource.network.response.SchoolResponse
import com.schoolconnect.app.feature.schools.SchoolRowUi
import com.schoolconnect.app.repository.analytics.SchoolAnalyticsRepository
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.school.SchoolRepository
import com.schoolconnect.app.repository.user.UserRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class SchoolDetailsViewModel(
    school: SchoolRowUi? = null
) {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] SchoolDetailsViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val initialState = SchoolDetailsUiState.fromSchool(school)
    private val _uiState = MutableStateFlow(
        initialState.copy(isSchoolLogoLoading = initialState.schoolLogoUrl.isNotBlank())
    )

    val uiState: StateFlow<SchoolDetailsUiState> = _uiState.asStateFlow()

    init {
        loadSchool(initialState.schoolCode)
    }

    private fun loadSchool(schoolCode: String) {
        val normalizedCode = schoolCode.trim()
        if (normalizedCode.isBlank()) {
            _uiState.update { it.copy(isLoading = false) }
            return
        }
        _uiState.update { it.copy(isLoading = true, errorMessage = "") }
        scope.launch {
            SchoolRepository.loadSchoolByCode(normalizedCode, forceRemote = true).fold(
                onSuccess = { school ->
                    val loaded = SchoolDetailsUiState.fromResponse(school)
                    _uiState.update {
                        loaded.copy(
                            analytics = it.analytics,
                            isAnalyticsLoading = it.isAnalyticsLoading,
                            isLoading = false
                        )
                    }
                    loadSchoolLogo(loaded.schoolLogoUrl)
                    loadSchoolAnalytics(loaded.schoolCode)
                    loadActorName(
                        displayName = loaded.createdBy,
                        userId = school.createdById.orEmpty(),
                        isCreatedBy = true
                    )
                    loadActorName(
                        displayName = loaded.updatedBy,
                        userId = school.updatedById.orEmpty(),
                        isCreatedBy = false
                    )
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Unable to load school details."
                        )
                    }
                    scLog("$TAG school load failure code=$normalizedCode", throwable)
                }
            )
        }
    }

    private fun loadActorName(displayName: String, userId: String, isCreatedBy: Boolean) {
        if (displayName.isNotBlank()) return
        loadUserName(userId, isCreatedBy)
    }

    private fun loadUserName(userId: String, isCreatedBy: Boolean) {
        val normalizedUserId = userId.trim()
        if (normalizedUserId.isBlank()) {
            _uiState.update { if (isCreatedBy) it.copy(createdBy = "—") else it.copy(updatedBy = "—") }
            return
        }
        _uiState.update { if (isCreatedBy) it.copy(isCreatedByLoading = true) else it.copy(isUpdatedByLoading = true) }
        scope.launch {
            UserRepository.loadUserDisplayName(normalizedUserId).fold(
                onSuccess = { name ->
                    _uiState.update {
                        if (isCreatedBy) it.copy(createdBy = name.ifBlank { "—" }, isCreatedByLoading = false)
                        else it.copy(updatedBy = name.ifBlank { "—" }, isUpdatedByLoading = false)
                    }
                },
                onFailure = { throwable ->
                    _uiState.update {
                        if (isCreatedBy) it.copy(createdBy = "—", isCreatedByLoading = false)
                        else it.copy(updatedBy = "—", isUpdatedByLoading = false)
                    }
                    scLog("$TAG user name load failure field=${if (isCreatedBy) "createdBy" else "updatedBy"}", throwable)
                }
            )
        }
    }

    private fun loadSchoolAnalytics(schoolCode: String) {
        scope.launch {
            _uiState.update { it.copy(isAnalyticsLoading = true) }
            SchoolAnalyticsRepository.loadSchoolAnalytics(schoolCode, forceRemote = true).fold(
                onSuccess = { analytics ->
                    _uiState.update {
                        it.copy(analytics = analytics, isAnalyticsLoading = false)
                    }
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isAnalyticsLoading = false,
                            errorMessage = "Unable to load school analytics."
                        )
                    }
                    scLog("$TAG analytics load failure school=${schoolCode.ifBlank { "<blank>" }}", throwable)
                }
            )
        }
    }

    fun onEvent(event: SchoolDetailsUiEvent) {
        when (event) {
            SchoolDetailsUiEvent.BackClicked -> Unit
            SchoolDetailsUiEvent.CloseClicked -> Unit
        }
    }

    private fun loadSchoolLogo(url: String) {
        val normalizedUrl = url.trim()
        if (normalizedUrl.isBlank()) {
            _uiState.update { it.copy(isSchoolLogoLoading = false) }
            return
        }
        scope.launch {
            FileUploadRepository.downloadBytes(normalizedUrl).fold(
                onSuccess = { bytes ->
                    _uiState.update {
                        it.copy(schoolLogoBytes = bytes, isSchoolLogoLoading = false)
                    }
                    scLog("$TAG logo load success code=${itCode(_uiState.value.schoolCode)}")
                },
                onFailure = { throwable ->
                    _uiState.update { it.copy(isSchoolLogoLoading = false) }
                    scLog("$TAG logo load failure code=${_uiState.value.schoolCode}", throwable)
                }
            )
        }
    }

    private fun itCode(value: String): String = value.ifBlank { "<blank>" }

    fun clear() {
        scope.cancel()
    }
}
